'use client';

import { InstantSearch, Configure } from 'react-instantsearch';
import { searchClient, EVENTS_INDEX } from '@/lib/algolia/config';
import routingConfig from '@/lib/algolia/routing';
import SearchBannerNew from './SearchBannerNew';
import EventsListNew from './EventsListNew';
import history from 'instantsearch.js/es/lib/routers/history';
import { useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

interface FindEventsContentNewProps {
  dict: any;
  currentLang: string;
}

export default function FindEventsContentNew({ dict, currentLang }: FindEventsContentNewProps) {
  const searchParams = useSearchParams();
  
  // Debug: Log search params
  console.log('Search params:', searchParams.toString());
  console.log('All search params:', Object.fromEntries(searchParams.entries()));
  
  // Create Configure parameters from URL search params
  const configureParams = useMemo(() => {
    const params: any = {
      hitsPerPage: 6,
      clickAnalytics: true,
    };

    // Handle facet filters from URL parameters
    const facetFilters: string[][] = [];
    
    // Handle fees parameter - check for both 'fees' and 'fees[0]' formats
    let fees = searchParams.get('fees');
    if (!fees) {
      fees = searchParams.get('fees[0]');
    }
    console.log('Fees parameter:', fees);
    if (fees) {
      const feesArray = Array.isArray(fees) ? fees : [fees];
      const feesFilters = feesArray.map(fee => `eventFees:${fee}`);
      facetFilters.push(feesFilters);
      console.log('Added fees filters:', feesFilters);
    }
    
    // Handle region parameter - check for both 'region' and 'region[0]' formats
    let region = searchParams.get('region');
    if (!region) {
      region = searchParams.get('region[0]');
    }
    if (region) {
      const regionArray = Array.isArray(region) ? region : [region];
      const regionFilters = regionArray.map(reg => `eventRegions:${reg}`);
      facetFilters.push(regionFilters);
    }
    
    // Handle city parameter - check for both 'city' and 'city[0]' formats
    let city = searchParams.get('city');
    if (!city) {
      city = searchParams.get('city[0]');
    }
    if (city) {
      const cityArray = Array.isArray(city) ? city : [city];
      const cityFilters = cityArray.map(c => `contactInformations.address.city:${c}`);
      facetFilters.push(cityFilters);
    }
    
    // Handle organizerCity parameter - check for both 'organizerCity' and 'organizerCity[0]' formats
    let organizerCity = searchParams.get('organizerCity');
    if (!organizerCity) {
      organizerCity = searchParams.get('organizerCity[0]');
    }
    if (organizerCity) {
      const organizerCityArray = Array.isArray(organizerCity) ? organizerCity : [organizerCity];
      const organizerCityFilters = organizerCityArray.map(oc => `organiser.address.city:${oc}`);
      facetFilters.push(organizerCityFilters);
    }
    
    // Handle zip parameter - check for both 'zip' and 'zip[0]' formats
    let zip = searchParams.get('zip');
    if (!zip) {
      zip = searchParams.get('zip[0]');
    }
    if (zip) {
      const zipArray = Array.isArray(zip) ? zip : [zip];
      const zipFilters = zipArray.map(z => `contactInformations.address.zip:${z}`);
      facetFilters.push(zipFilters);
    }
    
    // Handle category parameter - check for both 'category' and 'category[0]' formats
    let category = searchParams.get('category');
    if (!category) {
      category = searchParams.get('category[0]');
    }
    if (category) {
      const categoryArray = Array.isArray(category) ? category : [category];
      const categoryFilters = categoryArray.map(cat => `categories.name:${cat}`);
      facetFilters.push(categoryFilters);
    }
    
    console.log('Final facet filters:', facetFilters);
    if (facetFilters.length > 0) {
      params.facetFilters = facetFilters;
    }
    
    console.log('Configure params:', params);
    return params;
  }, [searchParams]);

  const routing = {
    router: history({
      cleanUrlOnDispose: false,
    }),
    stateMapping: routingConfig(EVENTS_INDEX),
  }
  
  return (
    <div style={{ animation: 'fadeIn 0.3s ease-in' }}>
      <InstantSearch 
        future={{ preserveSharedStateOnUnmount: true }}
        searchClient={searchClient} 
        indexName={EVENTS_INDEX}
        routing={routing} 
      >
        <Configure {...configureParams} />
        
        {/* Search Banner - contains only the banner and search form */}
        <SearchBannerNew dict={dict} />
        
        {/* Events List - contains the event cards grid and pagination */}
        <EventsListNew dict={dict} />
      </InstantSearch>
    </div>
  );
}
